import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonButton, IonIcon, ToastController } from '@ionic/angular';
import { addIcons } from 'ionicons';
import { closeOutline, fingerPrintOutline } from 'ionicons/icons';
import { Subject, takeUntil } from 'rxjs';

import { PasskeyPromptService } from '../../services/passkey-prompt.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService, TranslationParams } from '../../services/translation.service';

/**
 * Global "Sign in faster with a passkey" sheet, mounted once in the app
 * shell. Visibility is entirely driven by `PasskeyPromptService.visible$`,
 * which only ever goes true right after a normal (non-passkey) login lands.
 */
@Component({
  selector: 'app-passkey-prompt',
  standalone: true,
  imports: [CommonModule, IonButton, IonIcon, TranslatePipe],
  templateUrl: './passkey-prompt.component.html',
  styleUrls: ['./passkey-prompt.component.scss']
})
export class PasskeyPromptComponent implements OnInit, OnDestroy {
  visible = false;
  isWorking = false;

  private destroy$ = new Subject<void>();

  constructor(
    private passkeyPromptService: PasskeyPromptService,
    private toastController: ToastController,
    @Optional() private translationService?: TranslationService
  ) {
    addIcons({ closeOutline, fingerPrintOutline });
  }

  ngOnInit(): void {
    this.passkeyPromptService.visible$
      .pipe(takeUntil(this.destroy$))
      .subscribe(visible => this.visible = visible);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async accept(): Promise<void> {
    this.isWorking = true;
    try {
      const result = await this.passkeyPromptService.accept();
      const toast = await this.toastController.create({
        message: result.success
          ? this.t('passkey.addedUseNextSignIn', 'Passkey added — you can use it next time you sign in.')
          : this.t('passkey.couldNotAddPasskeyNow', 'Could not add a passkey right now.'),
        duration: 3000,
        color: result.success ? 'success' : 'danger',
        position: 'top'
      });
      await toast.present();
    } finally {
      this.isWorking = false;
    }
  }

  dismiss(): void {
    this.passkeyPromptService.dismiss();
  }

  private t(key: string, fallback: string, params?: TranslationParams): string {
    const value = this.translationService?.getTranslation(key, params);
    if (value && value !== key) {
      return value;
    }

    if (!params) {
      return fallback;
    }

    return Object.keys(params).reduce(
      (text, param) => text.replace(new RegExp(`\\{\\{${param}\\}\\}`, 'g'), () => String(params[param] ?? '')),
      fallback
    );
  }
}
